// Read the shipped stylesheet rather than maintaining a second token whitelist.
// Keep selector and conditional context: component-local variables need to be
// overridden on that component, not just inherited from :root.
export function stylesheetTokens(doc, {includeWorkspace = true, sort = true} = {}) {
  const tokens = new Map()
  function visit(rules, conditions = [], parentSelector = null) {
    for (const rule of rules) {
      let selector = rule.selectorText
      if (selector && parentSelector) selector = selector.includes('&')
        ? selector.replaceAll('&', `:is(${parentSelector})`)
        : `:is(${parentSelector}) :is(${selector})`
      if (rule.selectorText && rule.style) {
        for (const name of Array.from(rule.style)) {
          if (!name.startsWith('--') || name.startsWith('--tw-')) continue
          const token = {name, value: rule.style.getPropertyValue(name).trim(), selector, conditions}
          tokens.set(JSON.stringify([name, token.selector, conditions]), token)
        }
      }
      if (rule.cssRules) {
        // Layers are intentionally omitted: the editor's priority mode manages
        // layering. Media/supports/container conditions retain their meaning.
        const conditional = /^@(media|supports|container|scope)\b/.test(rule.cssText)
        const context = conditional ? [...conditions, rule.cssText.slice(0, rule.cssText.indexOf('{')).trim()] : conditions
        visit(rule.cssRules, context, selector || parentSelector)
      }
    }
  }
  for (const sheet of Array.from(doc.styleSheets)) {
    if (!sheet.href && !(includeWorkspace && sheet.ownerNode?.id === 'brand-preview-vars')) continue
    try { visit(sheet.cssRules) } catch (_) { /* Cross-origin sheets are not readable. */ }
  }
  const result = [...tokens.values()]
  return sort ? result.sort((a, b) => a.name.localeCompare(b.name) || a.selector.localeCompare(b.selector)) : result
}

export function tokenRule(token) {
  let source = `${token.selector} {\n  ${token.name}: ${token.value};\n}`
  for (const condition of [...token.conditions].reverse()) source = `${condition} {\n${source.split('\n').map(line => `  ${line}`).join('\n')}\n}`
  return source
}

// The starter comes from the shipped Mogno CSS, never workspace overrides or
// an invented palette. Keep the original declarations in comments for reset.
export function officialStylesheet(doc) {
  const groups = new Map()
  for (const token of stylesheetTokens(doc, {includeWorkspace: false, sort: false})) {
    const selectors = token.selector.split(',').map(selector => selector.trim())
    if (!selectors.every(selector => [':root', ':host', 'html', 'html.dark', '.dark'].includes(selector))) continue
    const selector = selectors.some(selector => selector.includes('.dark')) ? 'html.dark' : ':root'
    const key = JSON.stringify([selector, token.conditions])
    if (!groups.has(key)) groups.set(key, {selector, conditions: token.conditions, values: new Map()})
    groups.get(key).values.set(token.name, token.value)
  }
  const defaults = groups.get(JSON.stringify([':root', []]))?.values || new Map()
  const resolve = (value, values, seen = new Set()) => {
    const name = /^var\((--[\w-]+)\)$/.exec(value)?.[1]
    if (!name || seen.has(name) || !values.has(name)) return value
    return resolve(values.get(name), values, new Set([...seen, name]))
  }
  return [...groups.values()].sort((a, b) => Number(a.selector !== ':root') - Number(b.selector !== ':root')).map(group => {
    const values = new Map([...defaults, ...group.values])
    let rule = `${group.selector} {\n${[...group.values].map(([name, original]) => { const value = resolve(original, values); return `  ${name}: ${value}; /* Mogno: ${value.replaceAll('*/', '* /')} */` }).join('\n')}\n}`
    for (const condition of [...group.conditions].reverse()) rule = `${condition} {\n${rule.split('\n').map(line => `  ${line}`).join('\n')}\n}`
    return rule
  }).join('\n\n') + '\n'
}
